import React, { useEffect, useState } from "react";
import TreeView, { flattenTree } from "react-accessible-treeview";
import "./treeView.css";
import { FetchCategories } from "../api/assetCategoriesService";
import { useGlobalProvider } from '../contexts/GlobalProvider';
import { useAuth } from '../contexts/AuthContext';

function buildNestedStructure(data) {
    const nodeMap = {};
    let root = null;




    data.forEach(item => {
        if (item.isRoot) {
            item.parentId = null;
        } else if (!item.hasOwnProperty('parentId')) {
            item.parentId = undefined;
        }
        nodeMap[item.id] = { ...item, children: [] };
    });

    data.forEach(item => {
        if (item.parentId === null) {
            root = nodeMap[item.id];
        } else if (nodeMap[item.parentId]) {
            nodeMap[item.parentId].children.push(nodeMap[item.id]);
        }
    });

    return root;
}

function CategoryTree({ tempSelectedCategoryId, setTempSelectedCategoryId }) {
    const { selectedProjectId, selectedCategoryId, selectedHubId, setSelectedCategoryId } = useGlobalProvider(); // Ensure correct usage of useGlobalProvider

    // Use temp selection if provided (for modal), otherwise use global selection
    const currentSelectedCategoryId = tempSelectedCategoryId !== undefined ? tempSelectedCategoryId : selectedCategoryId;
    const setCurrentSelectedCategoryId = setTempSelectedCategoryId || setSelectedCategoryId;
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const { getAccessToken } = useAuth();

    // useEffect to watch for changes in selected hubs and clear the category tree

    // Clear out categories and selectedCategory whenever hub changes:
    useEffect(() => {
        if (!selectedProjectId || selectedProjectId.trim() === '') return;
        setIsLoading(true);
        setError(null);
        (async () => {
            try {
                const token = await getAccessToken();
                const { results } = await FetchCategories({ selectedProjectId, accessToken: token });
                setCategories(Array.isArray(results) ? results : []);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [selectedProjectId, selectedHubId]);

    useEffect(() => {
        if (!selectedProjectId || selectedProjectId.trim() === '') {
            // console.log("No projectId available yet");
            return;
        } else {
            console.log("Project ID in CategoryTree:", selectedProjectId);
        }

        const fetchCategories = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const accessToken = await getAccessToken();
                // console.log('Token in category tree:', accessToken);
                const data = await FetchCategories({ selectedProjectId: selectedProjectId, accessToken: accessToken });
                if (!data || !data.results || !Array.isArray(data.results)) {
                    throw new Error('Invalid or empty data returned from API');
                }
                setCategories(data.results);
            } catch (error) {
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCategories();
    }, [selectedProjectId]);



    const structuredData = buildNestedStructure(categories);
    const data = structuredData ? flattenTree(structuredData) : [];

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center p-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <span className="ms-2">Loading categories...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Error loading categories: {error}
            </div>
        );
    }

    // console.log('Data categories:', categories);
    if (categories.length === 0 || (categories.length === 1 && categories[0].name === "ROOT")) {
        return (
            <div className="alert alert-warning" role="alert">
                <i className="bi bi-folder-x me-2"></i>
                No asset categories found in this project
            </div>
        );
    }
    if (!selectedProjectId) {
        return
    }
    // Find the selected node IDs for TreeView
    const selectedIds = currentSelectedCategoryId ? [currentSelectedCategoryId] : [];
    
    return (
        <div>
            {/* Clear Selection Button */}
            <div className="mb-3">
                <button
                    type="button"
                    className={`btn btn-sm ${currentSelectedCategoryId === '1' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => {
                        setCurrentSelectedCategoryId('1');
                        console.log("Selection cleared - showing all assets (category ID: 1)");
                    }}
                >
                    <i className="bi bi-folder-x me-1"></i>
                    {currentSelectedCategoryId === '1' ? 'All Assets Selected' : 'Clear Selection (Show All Assets)'}
                </button>
                {currentSelectedCategoryId !== '1' && (
                    <small className="text-muted ms-2">
                        Click to show assets from all categories
                    </small>
                )}
            </div>
            
            <div className="checkbox">
                {/*{selectedProjectId && <p>Project ID in category tree view: {selectedProjectId}</p>}*/}
                <TreeView
                    data={data}
                    aria-label="Single select"
                    multiSelect={false}
                    propagateSelectUpwards
                    togglableSelect
                    nodeAction="check"
                    selectedIds={selectedIds}
                    nodeRenderer={({ element, isBranch, isExpanded, isSelected, getNodeProps, level, handleSelect, handleExpand }) => (
                        <div
                            {...getNodeProps({
                                onClick: (event) => {
                                    // Update our category selection state
                                    if (currentSelectedCategoryId === element.id) {
                                        // Deselect - set to root category (1) for all assets
                                        setCurrentSelectedCategoryId('1');
                                        console.log("Category deselected, set to root category (1)");
                                    } else {
                                        setCurrentSelectedCategoryId(element.id);
                                        console.log("Category selected:", element.id, "element name:", element.name);
                                    }
                                    // Let TreeView handle its own selection
                                    handleSelect(event);
                                    handleExpand(event);
                                }
                            })}
                            className={`d-flex align-items-center py-1 px-2 rounded ${isSelected ? 'bg-primary bg-opacity-10 border border-primary border-opacity-25' : ''} tree-node-item`}
                            style={{ 
                                marginLeft: 20 * (level - 1),
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {isBranch && (
                                <span className="me-2" style={{ width: '16px', textAlign: 'center' }}>
                                    <ArrowIcon isopen={isExpanded} />
                                </span>
                            )}
                            {!isBranch && <span className="me-2" style={{ width: '16px' }}></span>}
                            
                            <i 
                                className={`bi ${isSelected ? 'bi-folder-fill' : 'bi-folder'} me-2`}
                                style={{ 
                                    color: isSelected ? 'var(--bs-primary)' : 'var(--bs-secondary)',
                                    fontSize: '16px'
                                }}
                            />
                            
                            <span className={`flex-grow-1 ${isSelected ? 'fw-semibold text-primary' : ''}`}>
                                {element.name}
                            </span>
                        </div>
                    )}
                />
            </div>
        </div>
    );
}

const ArrowIcon = ({ isopen }) => (
    <i 
        className={`bi ${isopen ? 'bi-chevron-down' : 'bi-chevron-right'}`}
        style={{ 
            transition: 'transform 0.2s ease',
            fontSize: '14px',
            color: 'var(--bs-secondary)'
        }}
    />
);



export default CategoryTree;
